import { ContentItem } from '@superfunnel/types';
import {
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { emptyConverter } from '../utils';
import { UserContext } from './UserContext';

interface AccountContentContextProps {
  contents?: QueryDocumentSnapshot<ContentItem, ContentItem>[];
  isLoadingContents: boolean;

  createContentItem: (data: ContentItem) => Promise<ContentItem>;
  updateContentItem: (id: string, data: ContentItem) => Promise<ContentItem>;
  deleteContentItem: (id: string) => Promise<void>;
}

export const AccountContentContext = createContext<AccountContentContextProps>({
  isLoadingContents: false,

  createContentItem: async () => {
    throw new Error('unimplemeneted');
  },
  updateContentItem: async () => {
    throw new Error('unimplemeneted');
  },
  deleteContentItem: async () => {},
});

export function AccountContentContextProvider(props: any) {
  const { selectedAccount } = useContext(UserContext);
  const [contents, setContents] =
    useState<QueryDocumentSnapshot<ContentItem, ContentItem>[]>();
  const [isLoadingContents, setIsLoadingContents] = useState(false);

  useEffect(() => {
    if (!selectedAccount) {
      setContents(undefined);
      return;
    }

    setIsLoadingContents(true);

    const contentsColRef = collection(
      selectedAccount.ref,
      'contents',
    ).withConverter(emptyConverter<ContentItem>());
    const unsubscribe = onSnapshot(
      query(
        contentsColRef,
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
      ),
      (contents) => {
        setContents(contents.docs);
        setIsLoadingContents(false);
      },
      (e) => {
        console.error(e);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const createContentItem = useCallback(
    async (item: ContentItem) => {
      if (!selectedAccount) {
        throw new Error('no account found');
      }

      const docRef = doc(collection(selectedAccount.ref, 'contents'));
      await setDoc(docRef, {
        status: 'active',
        createdAt: Timestamp.now(),
        ...item,
        id: docRef.id,
      });

      return item;
    },
    [selectedAccount],
  );

  const updateContentItem = useCallback(
    async (id: string, item: ContentItem) => {
      if (!selectedAccount) {
        throw new Error('no account found');
      }

      await setDoc(
        doc(collection(selectedAccount.ref, 'contents'), id),
        {
          status: 'active',
          createdAt: Timestamp.now(),
          ...item,
          updatedAt: Timestamp.now(),
        },
        { merge: true },
      );

      return item;
    },
    [selectedAccount],
  );

  const deleteContentItem = useCallback(
    async (id: string) => {
      if (!selectedAccount) {
        throw new Error('no account found');
      }

      await setDoc(
        doc(collection(selectedAccount.ref, 'contents'), id),
        {
          status: 'deleted',
        },
        { merge: true },
      );
    },
    [selectedAccount],
  );

  return (
    <AccountContentContext.Provider
      value={{
        contents,
        isLoadingContents,

        createContentItem,
        updateContentItem,
        deleteContentItem,
      }}
    >
      {props.children}
    </AccountContentContext.Provider>
  );
}
